/**
 * Pure state helpers for the ICP-MS Mass Pick batch grid.
 *
 * ALPACA runs this screen as a DevExpress batch-edit grid (`InlineEditMode="Batch"`
 * on ICP_MS_MassPick_ListView, with an empty DetailViewID — there is no detail form).
 * You add rows, type across cells, and one "Save changes" commits the lot. Everything
 * here is the buffering side of that: it never touches the network, so the rules can
 * be tested directly.
 */

/** The composite key ALPACA's grid lookup writes, "Matrix|~|Method". */
export const PAIR_KEY_SEP = '|~|';
/** How the derived Matrix / Method / TestMethodKeys lists are joined in the columns. */
export const VALUE_SEP = ';';

/** One (Matrix, Method) pair offered by the picker. */
export interface TestMethodPair {
  key: string;
  matrixName: string;
  methodNumber: string;
}

/** A row as the grid holds it — server shape plus the two client-only fields. */
export interface MassPickRow {
  /** Real Oid, or a `new:N` placeholder for a row that has not been saved yet. */
  id: string;
  isNew: boolean;
  parameter: string;
  parameterName: string;
  element: string;
  method: string;
  matrix: string;
  testMethodKeys: string;
  // A mass is always a NUMBER, never blank. ALPACA declares Mass1..4 as plain
  // `int` (not int?), so XPO reads a NULL column as 0 and the grid shows 0 — a
  // blank mass is not representable there. Some live rows do hold NULL, which is
  // simply "never set"; showing them empty while their neighbours showed 0 read as
  // missing data (owner report 2026-09-25).
  selectMass1: boolean; mass1: number;
  selectMass2: boolean; mass2: number;
  selectMass3: boolean; mass3: number;
  selectMass4: boolean; mass4: number;
}

export type MassFlag = 'selectMass1' | 'selectMass2' | 'selectMass3' | 'selectMass4';
export const MASS_FLAGS: MassFlag[] = ['selectMass1', 'selectMass2', 'selectMass3', 'selectMass4'];

/** A blank row, as ALPACA's AddNew creates it — every mass flag explicitly false. */
export function blankRow(seq: number): MassPickRow {
  return {
    id: `new:${seq}`,
    isNew: true,
    parameter: '', parameterName: '', element: '',
    method: '', matrix: '', testMethodKeys: '',
    selectMass1: false, mass1: 0,
    selectMass2: false, mass2: 0,
    selectMass3: false, mass3: 0,
    selectMass4: false, mass4: 0,
  };
}

/**
 * ALPACA's AddNew guard: it refuses when a row with no Parameter is already on the
 * grid, so blank rows cannot stack up. Parameter is the row's identity here
 * ([RuleRequiredField] + [RuleUniqueValue]), so an unfilled one is the row you were
 * already meant to be completing.
 */
export function canAddRow(rows: readonly MassPickRow[]): boolean {
  return !rows.some((r) => !r.parameter);
}

/**
 * Ticking one mass unticks the other three — ALPACA does this client-side in
 * BatchEditStartEditing, and the API rejects a row with more than one set. Applied
 * to the row rather than the grid so it holds however the value was reached.
 */
export function applyMassFlag(row: MassPickRow, flag: MassFlag, on: boolean): MassPickRow {
  const next = { ...row };
  for (const f of MASS_FLAGS) next[f] = on && f === flag;
  return next;
}

/**
 * The picked pairs, folded into the three columns the way ALPACA's grid lookup does:
 * Matrix and Method each carry the DISTINCT values of the selection (so they stay in
 * step no matter which column was clicked), and TestMethodKeys carries the composites.
 */
export function applyPairs(row: MassPickRow, pairs: readonly TestMethodPair[]): MassPickRow {
  const distinct = (xs: string[]) => [...new Set(xs)].sort();
  return {
    ...row,
    matrix: distinct(pairs.map((p) => p.matrixName)).join(`${VALUE_SEP} `),
    method: distinct(pairs.map((p) => p.methodNumber)).join(`${VALUE_SEP} `),
    testMethodKeys: pairs.map((p) => p.key).join(VALUE_SEP),
  };
}

/** The keys currently on a row, for re-ticking the picker when it reopens. */
export function pairKeysOf(row: MassPickRow): string[] {
  return (row.testMethodKeys ?? '')
    .split(VALUE_SEP)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Rebuilds the selection from the stored Matrix + Method text when TestMethodKeys is
 * empty. Every row in the live table predates the keys column, so without this an
 * existing row would open the picker with nothing ticked and silently lose its values
 * on the next save. ALPACA has the same fallback (ReconstructTestMethodOids): match
 * the pairs whose matrix AND method are both among the row's saved values.
 */
export function reconstructPairKeys(row: MassPickRow, pairs: readonly TestMethodPair[]): string[] {
  const stored = pairKeysOf(row);
  if (stored.length > 0) return stored;

  const split = (v: string) => new Set(
    (v ?? '').split(VALUE_SEP).map((s) => s.trim()).filter((s) => s.length > 0),
  );
  const matrices = split(row.matrix);
  const methods = split(row.method);
  if (matrices.size === 0 || methods.size === 0) return [];

  return pairs
    .filter((p) => matrices.has(p.matrixName) && methods.has(p.methodNumber))
    .map((p) => p.key);
}

/** Does the grid hold anything that "Save changes" would commit? */
export function isDirty(
  rows: readonly MassPickRow[],
  original: ReadonlyMap<string, MassPickRow>,
  deleted: ReadonlySet<string>,
): boolean {
  if (deleted.size > 0) return true;
  return rows.some((r) => {
    if (r.isNew) return true;
    const was = original.get(r.id);
    return !was || !sameRow(was, r);
  });
}

/** Field-by-field comparison — what decides whether a row is sent on save. */
export function sameRow(a: MassPickRow, b: MassPickRow): boolean {
  return a.parameter === b.parameter
    && a.element === b.element
    && a.method === b.method
    && a.matrix === b.matrix
    && a.testMethodKeys === b.testMethodKeys
    && MASS_FLAGS.every((f) => a[f] === b[f])
    && a.mass1 === b.mass1 && a.mass2 === b.mass2
    && a.mass3 === b.mass3 && a.mass4 === b.mass4;
}

/**
 * The rows "Save changes" actually sends: the new ones plus the genuinely changed
 * ones. An untouched row is left out, so a save never rewrites (or re-audits) a row
 * the user did not edit.
 */
export function changedRows(
  rows: readonly MassPickRow[],
  original: ReadonlyMap<string, MassPickRow>,
  deleted: ReadonlySet<string>,
): MassPickRow[] {
  return rows.filter((r) => {
    if (deleted.has(r.id)) return false;
    if (r.isNew) return true;
    const was = original.get(r.id);
    return !was || !sameRow(was, r);
  });
}

/** A row in the shape the batch endpoint takes. */
export function toBatchRow(r: MassPickRow) {
  return {
    oid: r.isNew ? null : r.id,
    parameter: r.parameter || null,
    element: r.element?.trim() || null,
    method: r.method?.trim() || null,
    matrix: r.matrix?.trim() || null,
    testMethodKeys: r.testMethodKeys?.trim() || null,
    selectMass1: r.selectMass1, mass1: r.mass1,
    selectMass2: r.selectMass2, mass2: r.mass2,
    selectMass3: r.selectMass3, mass3: r.mass3,
    selectMass4: r.selectMass4, mass4: r.mass4,
  };
}

/** A whole-number mass. Clearing the cell gives 0, not blank — ALPACA's Mass1..4
 *  are `int`, so there is no empty state to return to. `undefined` means the text
 *  was not a whole number: rejected rather than silently truncated, because the
 *  columns are int and the API refuses a fraction. */
export function parseMass(text: string): number | undefined {
  const t = (text ?? '').trim();
  if (t === '') return 0;
  const n = Number(t);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return undefined;
  return n;
}
